import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import { useScrollTabs } from "hooks";
import { Routes } from "router/routes";
import { TickIcon } from "assets/website";
import { ContactModal } from "features/landing/contactUs/contactModal";
import { SampleReportModal } from "../SampleReportModal";
import {
  roleCard1,
  roleCard2,
  roleCard3,
  roleCard4,
  roleCard5,
  roleCard6,
  usecases1,
  usecases2,
  usecases3,
  usecases4,
  report1,
  report2,
  report3,
} from "assets/website";

/* ─── Role assessment cards data ─── */
interface RoleCard {
  title: string;
  description: string;
  image: string;
}

const roleCards: RoleCard[] = [
  {
    title: "HR Manager",
    description:
      "See how we evaluate people management, organizational thinking, communication, and workplace leadership capabilities.",
    image: roleCard1,
  },
  {
    title: "Chief Operations Officer",
    description:
      "Experience how our system evaluates operational leadership, process optimization, strategic execution, and organizational management.",
    image: roleCard2,
  },
  {
    title: "Executive Assistant",
    description:
      "See how we assess organization, coordination, attention to detail, communication, and operational support skills.",
    image: roleCard3,
  },
  {
    title: "Recruiter",
    description:
      "Discover how our assessment measures talent evaluation, communication, hiring judgment, and recruitment effectiveness.",
    image: roleCard4,
  },
  {
    title: "Product Manager",
    description:
      "Experience how our system assesses product thinking, prioritization, execution judgment, and strategic decision-making.",
    image: roleCard5,
  },
  {
    title: "Chief Technical Officer",
    description:
      "Test how we evaluate technical leadership, systems thinking, innovation capacity, and execution oversight.",
    image: roleCard6,
  },
];

/* ─── "Precision is Built For" tabs ─── */
type FitTab = "builders" | "scalers" | "fixers" | "assessors";

interface FitCase {
  id: FitTab;
  label: string;
  title: string;
  description: string;
  image: string;
  bullets: string[];
}

const fitCases: FitCase[] = [
  {
    id: "builders",
    label: "Builders",
    title: "Build the Right Team from Day One",
    description:
      "Early hiring decisions shape everything – culture, speed, and long-term performance. Precision helps you move beyond “gut feeling” to a structured evaluation that gets you the right fit for your organisation and reduces costly mis-hires.",
    image: usecases1,
    bullets: [
      "Role-based assessments that evaluate candidates beyond CVs and interviews.",
      "Detailed report ranking candidates based on work skills, work culture, personality, technical skills and more.",
      "Candidate comparison for close-scoring candidates.",
      "Identify high-potential, execution-ready talent.",
      "Align hiring with business goals.",
    ],
  },
  {
    id: "scalers",
    label: "Scalers",
    title: "Standardise Hiring. Improve Talent Quality. Reduce Risk.",
    description:
      "HR teams are under pressure to hire faster, fairer, and smarter. Precision gives you a consistent evaluation framework across roles and departments.",
    image: usecases2,
    bullets: [
      "Standardised, role-specific assessments",
      "Detailed reports ranking candidates based on work skills, work culture, personality, technical skills and more",
      "Candidate comparison for close-scoring candidates based on real performance indicators",
      "Strengthen employer credibility",
    ],
  },
  {
    id: "fixers",
    label: "Fixers",
    title: "Build Capable Leaders.",
    description:
      "Precision supports government institutions in strengthening leadership quality and execution capacity; it also helps academies and training organisations measure real-world competence, all through structured talent intelligence.",
    image: usecases3,
    bullets: [
      "Structured assessments to test new candidates' employability and leadership potential",
      "Tests to track existing leaders' capability against set organisational goals",
      "Metrics to measure behavioural and professional skills",
      "Detailed impact reports with strategic recommendations",
    ],
  },
  {
    id: "assessors",
    label: "Assessors",
    title: "Deliver Better Shortlists. Win Client Trust. Increase Placements.",
    description:
      "Clients don't just want candidates; they want performers. Precision enables agencies to validate talent quality before submitting a shortlist to a client.",
    image: usecases4,
    bullets: [
      "Pre-screening candidates with structured assessments",
      "Detailed reports that aid an insight-rich shortlist of candidate profiles",
      "Reduced interview-to-hire cycles",
      "Increased placement success rates",
      "Agency differentiation",
    ],
  },
];

/* ─── "Find Your Workflow" sections ─── */
interface Workflow {
  heading: string;
  description: string;
  cta: string;
  image: string;
}

const workflows: Workflow[] = [
  {
    heading: "Choosing between strong candidates?",
    description:
      "When everyone looks good on paper, it’s hard to know who will actually perform. Precision compares candidates side-by-side, highlighting strengths, gaps, and true role fit, so you can make the right call with confidence.",
    cta: "Compare Candidates",
    image: report1,
  },
  {
    heading: "Don't have a job description?",
    description:
      "A JD alone doesn’t guarantee a good hire, how you evaluate against it matters. Precision analyzes each candidate against your role requirements, giving you structured, consistent insights beyond resumes and gut feel.",
    cta: "Evaluate Candidates",
    image: report2,
  },
  {
    heading: "Want a complete hiring system without the complexity?",
    description:
      "Managing hiring across tools, spreadsheets, and interviews can get messy fast. Precision brings everything into one streamlined flow—from role definition to final decision—so you can hire faster and smarter.",
    cta: "Start End-to-End Hiring",
    image: report3,
  },
];

/* ─── Closing "Contact Us" CTA for the workflow tab ─── */
const workflowCta = {
  heading: "Not sure what you’re looking for yet?",
  description:
    "Hiring without clarity leads to costly mistakes. Precision helps you define the role, identify what “good” looks like, and build a clear evaluation framework before you even start screening.",
  cta: "Contact Us",
};

type HeroTab = "assessment" | "workflow";

/* ─── Component ─── */
/* ─── Sparkle background ─── */
const SPARKLES = [
  { top: '13%', left: '46%',  size: 20, startLarge: false, duration: 3.4 }, // above hero title
  { top: '10%', left: '91%',  size: 14, startLarge: true,  duration: 2.8 }, // top right
  { top: '28%', left: '1%',   size: 28, startLarge: true,  duration: 3.8 }, // left side
  { top: '18%', left: '87%',  size: 18, startLarge: false, duration: 3.1 }, // right side
  { top: '52%', left: '5%',   size: 12, startLarge: false, duration: 2.6 }, // left lower
  { top: '65%', left: '93%',  size: 24, startLarge: true,  duration: 3.6 }, // right lower
  { top: '42%', left: '72%',  size: 16, startLarge: true,  duration: 3.0 }, // right of toggle
  { top: '55%', left: '46%',  size: 10, startLarge: false, duration: 2.9 }, // between toggle desc & cards
];

const SparkleSvg = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <polygon
      points="16,0 17.6,10.2 24,2.1 20.2,11.8 29.9,8 21.8,14.4 32,16 21.8,17.6 29.9,24 20.2,20.2 24,29.9 17.6,21.8 16,32 14.4,21.8 8,29.9 11.8,20.2 2.1,24 10.2,17.6 0,16 10.2,14.4 2.1,8 11.8,11.8 8,2.1 14.4,10.2"
      fill="#C6E0FF"
    />
  </svg>
);

const SparkleBackground = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {SPARKLES.map((s, i) => (
      <motion.div
        key={i}
        className="absolute"
        style={{ top: s.top, left: s.left }}
        animate={{ scale: s.startLarge ? [1.4, 0.6, 1.4] : [0.6, 1.4, 0.6] }}
        transition={{ duration: s.duration, repeat: Infinity, ease: "easeInOut" }}
      >
        <SparkleSvg size={s.size} />
      </motion.div>
    ))}
  </div>
);

const UseCasesUI = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // Both tab groups live in the URL so a view can be linked to and survives a refresh.
  const heroTab: HeroTab = params.get("tab") === "workflow" ? "workflow" : "assessment";
  const fitParam = params.get("for");
  const activeTab: FitTab = fitCases.some((c) => c.id === fitParam)
    ? (fitParam as FitTab)
    : "builders";

  const setParam = (key: string, value: string) => {
    params.set(key, value);
    setParams(params, { replace: true });
  };

  const setHeroTab = (next: HeroTab) => setParam("tab", next);
  const setActiveTab = (next: FitTab) => setParam("for", next);

  const [showContactModal, setShowContactModal] = useState(false);
  const [showSampleReportModal, setShowSampleReportModal] = useState(false);

  // The fit tabs are driven by the URL param, so bridge that to the index the
  // scroll handler works in. Scrolling or swiping over the panel steps through
  // them the same way the home page's Insights section does.
  const fitPanelRef = useRef<HTMLDivElement>(null);
  const activeFitIndex = fitCases.findIndex((c) => c.id === activeTab);

  useScrollTabs(fitPanelRef, activeFitIndex, fitCases.length, (next) =>
    setActiveTab(fitCases[next].id),
  );

  // The fit tabs overflow their row on narrow screens. Keep whichever tab is
  // active in view instead of leaving it stranded off the edge of the strip.
  const fitTabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const strip = fitTabsRef.current;
    const active = strip?.querySelector<HTMLElement>('[data-active="true"]');
    if (!strip || !active) return;

    // Scroll the strip itself rather than calling scrollIntoView on the button:
    // scrollIntoView also walks the vertical axis, so on first render it would
    // drag the whole page down to the tabs. Measuring off bounding rects keeps
    // this correct no matter which ancestor is the button's offsetParent.
    const stripRect = strip.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const offset = activeRect.left - stripRect.left - (stripRect.width - activeRect.width) / 2;

    strip.scrollTo({ left: strip.scrollLeft + offset, behavior: "smooth" });
  }, [activeTab]);

  useEffect(() => {
    AOS.init({ duration: 1000, offset: 100, easing: "ease-in-out", once: true });
    AOS.refresh();

    // The "Learn More" cards on the home page link straight to a tab of the
    // "Precision is Built For" section, so honour the hash rather than forcing
    // the visitor back to the top of the page.
    const target = window.location.hash
      ? document.getElementById(window.location.hash.slice(1))
      : null;

    if (!target) {
      window.scrollTo(0, 0);
      return;
    }

    // One frame so the section is laid out before we measure it.
    const raf = requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  const activeCase = fitCases.find((c) => c.id === activeTab)!;

  return (
    <div className="bg-[#F5FAFF] overflow-x-hidden">
      <div className="relative overflow-hidden">
        <SparkleBackground />
      {/* ────── Hero ────── */}
      <section className="pt-36 md:pt-52 pb-12 md:pb-16">
        <div className="max-w-[1200px] mx-auto lg:px-8 px-6 text-center">
          <h1
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-[28px] md:text-5xl lg:text-6xl font-display font-semibold text-[#00003F] lg:leading-[58px] md:leading-[40px] leading-[34px] mb-4"
          >
            Built for Any Role, Across Every Team
          </h1>

          <p
            data-aos="fade-up"
            data-aos-delay="200"
            className="text-base md:text-lg lg:text-xl xl:text-2xl text-[#475467] max-w-5xl mx-auto"
          >
            Precision Match isn&rsquo;t limited to one type of hire. Whether
            you&rsquo;re hiring for technical roles, operations, sales, or
            leadership, Precision adapts to the specific demands of each role, so
            you&rsquo;re always evaluating what actually matters.
          </p>

          {/* Hero tab toggle */}
          <div
            data-aos="fade-up"
            data-aos-delay="300"
            className="mt-20 flex justify-center"
          >
            <div className="inline-flex items-center p-1 rounded-full border-[2.5px] border-[#163B84]">
              <button
                onClick={() => setHeroTab("assessment")}
                className={`px-4 md:px-10 py-3 rounded-full text-sm md:text-base font-semibold transition-all duration-200 ${heroTab === "assessment"
                  ? "bg-[#012169] text-white"
                  : "text-[#050B17] hover:text-[#012169]"
                  }`}
              >
                Try Out an Assessment
              </button>
              <button
                onClick={() => setHeroTab("workflow")}
                className={`px-4 md:px-10 py-3 rounded-full text-sm md:text-base font-semibold transition-all duration-200 ${heroTab === "workflow"
                  ? "bg-[#012169] text-white"
                  : "text-[#050B17] hover:text-[#012169]"
                  }`}
              >
                Find Your Workflow
              </button>
            </div>
          </div>

          <p
            data-aos="fade-up"
            data-aos-delay="400"
            className="mt-6 text-base md:text-lg text-[#475467] max-w-xl mx-auto"
          >
            {heroTab === "assessment"
              && "Take a short sample assessment and instantly see how our system evaluates you, just like it would a candidate."}
          </p>
        </div>
      </section>

      {heroTab === "assessment" ? (
        <>
          {/* ────── Role Assessment Cards ────── */}
          <section className="pb-16 md:pb-20">
            <div className="max-w-[1280px] mx-auto lg:px-8 px-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {roleCards.map((card, index) => (
                  <div
                    key={card.title}
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                    className="p-5 rounded-2xl overflow-hidden bg-white group shadow-lg transition-shadow flex flex-col"
                  >
                    <div className="h-48 overflow-hidden rounded-xl">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="pt-4 flex flex-col flex-1">
                      <h3 className="text-2xl font-bold text-[#050B17] mb-2">
                        {card.title}
                      </h3>
                      <p className="text-base text-[#475467] leading-relaxed">
                        {card.description}
                      </p>
                      <button
                        onClick={() => navigate(Routes.signup)}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-[#012169] mt-auto pt-4 hover:gap-2 transition-all w-fit"
                      >
                        Take Assessment
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ────── Sample Report CTA ────── */}
          <section className="pb-12 md:pb-20">
            <div className="max-w-[1024px] mx-auto px-6 text-center">
              <h2
                data-aos="fade-up"
                className="text-xl md:text-2xl lg:text-3xl font-display font-bold text-[#050B17] mb-4"
              >
                See What a Full Precision Report Looks Like
              </h2>
              <p
                data-aos="fade-up"
                data-aos-delay="100"
                className="text-base md:text-lg text-[#475467] max-w-3xl mx-auto mb-8"
              >
                A full Precision report gives you a complete, multi-dimensional
                view of every candidate, so you can understand not just who they
                are, but how they&rsquo;ll perform in the role.
              </p>
              <button
                data-aos="fade-up"
                data-aos-delay="200"
                onClick={() => setShowSampleReportModal(true)}
                className="px-8 py-4 bg-[#012169] hover:bg-[#01154d] text-base text-white font-semibold rounded-lg shadow-[0px_4px_8px_0px_#01216940] transition-all duration-200 hover:shadow-lg"
              >
                View a Full Sample Report
              </button>
            </div>
          </section>
        </>
      ) : (
        /* ────── Find Your Workflow sections ────── */
        <section className="pb-16 md:pb-24">
          <div className="space-y-16 md:space-y-24">
            {workflows.map((wf, index) => 
              <div key={wf.heading} className=" border-t border-[#D8EAFF] rounded-lg py-6" data-aos="fade-up" data-aos-delay={index * 50}>
                <div className="max-w-[1280px] mx-auto lg:px-8 px-6">
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6 lg:gap-28 mb-8 md:mb-10 py-8">
                    <h3 className="text-3xl md:text-4xl lg:text-[52px] font-display font-semibold text-[#050B17] lg:leading-[1.1]">
                      {wf.heading}
                    </h3>
                    <div>
                      <p className="text-base md:text-xl text-[#475467] leading-relaxed mb-4">
                        {wf.description}
                      </p>
                      <button
                        onClick={() => navigate(Routes.signup)}
                        className="inline-flex items-center gap-1 text-[#175CD3] text-xl font-medium hover:gap-2 transition-all"
                      >
                        {wf.cta}
                        <ChevronRight className="w-5 h-5 -mb-1.5" />
                      </button>
                    </div>
                  </div>
                  <img
                    src={wf.image}
                    alt={`${wf.heading} report preview`}
                    className="w-full rounded-2xl"
                  />
                  </div>
              </div>
            )}

            {/* Closing centered Contact Us CTA */}
            <div
              className=""
              data-aos="fade-up"
            >
              <div className="max-w-[768px] mx-auto lg:px-8 px-6 text-center">
                <h3 className="text-3xl md:text-4xl lg:text-[40px] font-display font-semibold text-[#050B17] lg:leading-[1.1] mb-4">
                  {workflowCta.heading}
                </h3>
                <p className="text-base md:text-xl text-[#475467] leading-relaxed mb-8">
                  {workflowCta.description}
                </p>
                <button
                  onClick={() => setShowContactModal(true)}
                  className="px-8 py-4 bg-[#012169] hover:bg-[#01154d] text-base text-white font-semibold rounded-lg shadow-[0px_4px_8px_0px_#01216940] transition-all duration-200 hover:shadow-lg"
                >
                  {workflowCta.cta}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      </div>
      {/* ────── Precision is Built For (shown on both tabs) ────── */}
      <section id="precision-built-for" className="scroll-mt-28 py-20 md:py-28 bg-[#F9FAFB]">
        <div className="max-w-[1280px] mx-auto xl:px-8 lg:px-4 px-4">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <h2
              data-aos="fade-up"
              data-aos-delay="100"
              className="text-2xl md:text-4xl lg:text-5xl font-display font-bold text-[#050B17] mb-4"
            >
              Precision is Built For...
            </h2>
            <p
              data-aos="fade-up"
              data-aos-delay="200"
              className="text-base md:text-lg lg:text-xl text-[#475467] w-full lg:w-[80%] mx-auto"
            >
              Whether you&rsquo;re scaling or hiring for key roles, Precision
              helps bring consistency and structure to evaluation.
            </p>
          </div>

          {/* Tabs */}
          <div
            data-aos="fade-up"
            data-aos-delay="300"
            ref={fitTabsRef}
            className="flex overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {fitCases.map((fc) => (
              <button
                key={fc.id}
                data-active={activeTab === fc.id}
                onClick={() => setActiveTab(fc.id)}
                className={`shrink-0 pb-3 px-6 -mb-px text-base md:text-lg whitespace-nowrap transition-colors border-b-2 ${activeTab === fc.id
                  ? "text-[#012169] font-semibold border-[#FC9F4C]"
                  : "text-[#344054] font-normal border-transparent hover:text-[#012169]"
                  }`}
              >
                {fc.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div
            ref={fitPanelRef}
            data-aos="fade-up"
            data-aos-delay="400"
            className="bg-white rounded-lg py-4 md:py-7 px-5 md:px-12 mt-1"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">
              {/* Content (left) */}
              <div>
                <h3 className="text-lg sm:text-xl md:text-3xl font-bold text-neutral-900 mb-3 md:mb-4">
                  {activeCase.title}
                </h3>
                <p className="text-base md:text-xl text-[#475467] mb-4 md:mb-6 leading-relaxed">
                  {activeCase.description}
                </p>
                <p className="font-semibold text-neutral-900 mb-3 md:mb-4 text-base md:text-xl">
                  What you get:
                </p>
                <ul className="space-y-2 md:space-y-3">
                  {activeCase.bullets.map((bullet, i) => (
                    <li key={i} className="flex items-start gap-2 md:gap-3">
                      <TickIcon className="w-4 h-4 md:w-5 md:h-5 text-green-600 flex-shrink-0 mt-1" />
                      <span className="text-[#475467] text-base md:text-xl">
                        {bullet}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Image (right) */}
              <div>
                <img
                  src={activeCase.image}
                  alt={activeCase.title}
                  className="w-full rounded-lg h-[400px] md:h-[535px] object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactModal
        show={showContactModal}
        close={() => setShowContactModal(false)}
      />
      <SampleReportModal
        show={showSampleReportModal}
        onClose={() => setShowSampleReportModal(false)}
      />
    </div>
  );
};

export { UseCasesUI };
